import { DefinitionStat } from '@/components/common/definitionStat'
import type { BorrowMarketAssetData, BorrowMarketHistoricalDataPoint } from '@/utils/internalAppSdk'
import { formatDecimalToPercentage, formatUSD } from '@vaultsfyi/common/utils'
import type { ReactNode } from 'react'
import { BORROW_MARKET_DETAILS_STAT_VALUES, type BorrowMarketDetailsStat } from './borrowMarketDetailsStats.constants'
import { BorrowMarketDetailsStatsChart } from './borrowMarketDetailsStatsChart'

interface Props {
  borrowAssetData: BorrowMarketAssetData
  chartData: BorrowMarketHistoricalDataPoint[]
  isChartError: boolean
  isChartFetching: boolean
  stat: BorrowMarketDetailsStat
  rangeSelector?: ReactNode
  chartClassName: string
  showBrush?: boolean
}

interface DetailsStat {
  label: string
  value: string
}

const CONTENT_TITLES: Record<BorrowMarketDetailsStat, string> = {
  [BORROW_MARKET_DETAILS_STAT_VALUES.borrowApy]: 'Borrow APY - detailed view',
  [BORROW_MARKET_DETAILS_STAT_VALUES.utilization]: 'Utilization - detailed view',
  [BORROW_MARKET_DETAILS_STAT_VALUES.totalSupplied]: 'Total supplied - detailed view',
}

export const BorrowMarketDetailsStatsContent = ({
  borrowAssetData,
  chartData,
  isChartError,
  isChartFetching,
  stat,
  rangeSelector,
  chartClassName,
  showBrush = false,
}: Props) => (
  <div className="flex flex-col gap-4 pt-2">
    <h5 className="font-medium text-heading-foreground text-lg">{CONTENT_TITLES[stat]}</h5>
    <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
      <dl className="flex max-w-full shrink-0 flex-wrap gap-x-8 gap-y-3">
        {getDetailsStats({ borrowAssetData, stat }).map(({ label, value }) => (
          <DefinitionStat
            key={label}
            label={label}
            value={value}
            className="gap-0.5"
            labelClassName="font-normal text-body-foreground"
          />
        ))}
      </dl>
      {rangeSelector && <div className="shrink-0">{rangeSelector}</div>}
    </div>
    <BorrowMarketDetailsStatsChart
      data={chartData}
      stat={stat}
      className={chartClassName}
      isError={isChartError}
      isLoading={isChartFetching}
      isUnsupported={!borrowAssetData.supportsHistoricalData}
      showBrush={showBrush}
    />
  </div>
)

const getDetailsStats = ({
  borrowAssetData,
  stat,
}: {
  borrowAssetData: BorrowMarketAssetData
  stat: BorrowMarketDetailsStat
}): DetailsStat[] => {
  switch (stat) {
    case BORROW_MARKET_DETAILS_STAT_VALUES.borrowApy:
      return [
        {
          label: 'Current borrow APY',
          value:
            borrowAssetData.borrowRate !== undefined ? formatDecimalToPercentage(borrowAssetData.borrowRate) : '--',
        },
        {
          label: 'Supply APY',
          value:
            borrowAssetData.supplyRate !== undefined ? formatDecimalToPercentage(borrowAssetData.supplyRate) : '--',
        },
      ]
    case BORROW_MARKET_DETAILS_STAT_VALUES.utilization:
      return [
        {
          label: 'Utilization',
          value:
            borrowAssetData.utilizationRate !== undefined
              ? formatDecimalToPercentage(borrowAssetData.utilizationRate, { fractionDigits: 0 })
              : '--',
        },
        {
          label: 'Total borrowed',
          value: borrowAssetData.totalBorrowed?.usd ? formatUSD(borrowAssetData.totalBorrowed.usd) : '--',
        },
      ]
    case BORROW_MARKET_DETAILS_STAT_VALUES.totalSupplied:
      return [
        {
          label: 'Total supplied',
          value: borrowAssetData.totalSupplied?.usd ? formatUSD(borrowAssetData.totalSupplied.usd) : '--',
        },
        {
          label: 'Available to borrow',
          value: borrowAssetData.availableToBorrow?.usd ? formatUSD(borrowAssetData.availableToBorrow.usd) : '--',
        },
      ]
  }
}
